import { useCallback, useEffect } from 'react';
import Page from '../../../components/page/Page';
import {
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import useThemeMediaQuery from '../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../core/router/navigation';
import { useMeCore } from '../../../../core/modules/me';
import { useCompanyCore } from '../../../../core/modules/company';
import Header from '../../../components/operations/classifyAgain/Header';
import { useArticlesCore } from '../../../../core/modules/articles';
import { Article } from '../../../../core/interfaces/article.interface';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import { Controller, useForm } from 'react-hook-form';
import { useContainerCore } from '../../../../core/modules/container';
import { yupResolver } from '@hookform/resolvers/yup';

const ClassifyAgain = () => {
  const { user } = useMeCore();
  const { companyId } = useCompanyCore();
  const { articles, getArticles, setArticle } = useArticlesCore();
  const { containers, getContainers } = useContainerCore();
  const { goToWelcome } = useNavigationCore();

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { control } = useForm({
    mode: 'onChange',
    defaultValues: {
      container_id: 0,
      code: '',
    },
    resolver: yupResolver({ container_id: 0, code: '' } as any),
  });

  useEffect(() => {
    if (companyId) {
      getContainers(companyId, {});
    }
  }, [companyId, getContainers]);
  
  useEffect(() => {
    if (
      !user ||
      (!user.privileges.superAdmin && !user.privileges.writeMaster)
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  useEffect(() => {
    if (companyId) getArticles(companyId, {});
  }, [companyId, getArticles]);

  const selectFormat = useCallback(
    (article: Article) => {
      setArticle(article);
    },
    [setArticle]
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Descripción',
      flex: 1,
    },
    {
      field: 'code',
      headerName: 'Código',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <div className="my-auto">
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <TextField
                className="text-center"
                {...field}
                value={params.value}
                variant="standard"
                fullWidth
              />
            )}
          />
        </div>
      ),
    },
    {
      field: 'state',
      headerName: 'Estado',
      flex: 1,
    },
    {
      field: 'capacity',
      headerName: 'Cantidad',
      flex: 1,
    },
    {
      field: 'product_category_id',
      headerName: 'Sub Categoría',
      flex: 1,
    },
    {
      field: 'price',
      headerName: 'Precio Mercado',
      flex: 1,
    },
    {
      field: 'relocate-action',
      headerName: 'Ubicación',
      minWidth: 200,
      cellClassName: 'text-end',
      renderCell: (params: GridRenderCellParams) => (
        <div className=" w-full my-auto">
          <Controller
            control={control}
            name="container_id"
            render={({ field }) => (
              <FormControl variant="standard" fullWidth>
                <Select
                  className="text-center"
                  defaultValue={params.value}
                  {...field}
                  labelId="container-label"
                  label="Contenedor"
                >
                  {containers.map((container) => (
                    <MenuItem key={container.id} value={container.id}>
                      {container.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </div>
      ),
    },
    {
      field: 'valorize-action',
      headerName: 'Valorizar',
      cellClassName: 'text-end',
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          aria-label="valorize"
          onClick={() => selectFormat(params.row)}
        >
          <CurrencyExchangeIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Page
        header={<Header />}
        content={
          <div className="flex flex-col md:flex-row w-full my-16 px-6 md:px-24 lg:px-32 gap-6">
            {/* Tabla de datos */}
            <Paper className="flex flex-col flex-auto items-center shadow-lg rounded-2xl overflow-hidden">
              <div className="w-full p-6 md:p-12">
                <DataGrid
                  rows={articles}
                  columns={columns}
                  sx={{ border: 0 }}
                  disableColumnSorting
                  disableColumnFilter
                  disableRowSelectionOnClick
                  disableMultipleRowSelection
                  hideFooter
                  pagination={undefined}
                />
              </div>
            </Paper>

            {/* Otros contenedores */}
            <Paper className="w-full md:w-[300px] h-auto p-6 md:p-8 shadow-lg rounded-2xl border border-gray-200 bg-white">
              <Typography
                variant="h6"
                className="font-semibold text-gray-800 my-10 text-center"
              >
                Otros contenedores
              </Typography>

              {/* Selector */}
              <FormControl variant="outlined" fullWidth className="mb-4">
                <Select
                  className="text-center"
                  {...control.register('container_id')}
                  labelId="container-label"
                  label="Contenedor"
                >
                  {containers.map((container) => (
                    <MenuItem key={container.id} value={container.id}>
                      {container.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Lista de artículos */}
              <Paper className="p-4  rounded-lg ">
                {articles.length > 0 ? (
                  <ul className="space-y-2">
                    {articles.map((article, index) => (
                      <li
                        key={index}
                        className="p-4 bg-white rounded-lg    flex flex-col gap-1"
                      >
                        <Typography
                          variant="body1"
                          className="font-semibold text-gray-800"
                        >
                          {article.name} ({article.brand})
                        </Typography>
                        <Typography variant="body2" className="text-gray-600">
                          Código: {article.code}
                        </Typography>
                        {article.description && (
                          <Typography
                            variant="body2"
                            className="text-gray-500 text-sm italic"
                          >
                            {article.description}
                          </Typography>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Typography
                    variant="body2"
                    className="text-gray-500 text-center"
                  >
                    No hay artículos disponibles.
                  </Typography>
                )}
              </Paper>
            </Paper>
          </div>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />

     
    </>
  );
};

export default ClassifyAgain;
